import { useState } from "react";
import { redeemVoucher } from "../lib/hooks";
import type { WorkshopOption } from "../lib/types";

export type RedeemResult =
  | { type: "credit"; code: string; amountCents: number; balanceCents: number }
  | { type: "workshop"; code: string; kindLabel: string; booked: WorkshopOption };

interface RedeemVoucherProps {
  /** Called after a successful redemption; the parent decides where to go next. */
  onRedeemed: (result: RedeemResult) => void;
}

/** A workshop voucher that was recognised; the customer still has to pick a workshop. */
interface WorkshopChoice {
  code: string;
  kindLabel: string;
  options: WorkshopOption[];
}

export function formatWorkshopDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  const weekday = d.toLocaleDateString("sk-SK", { weekday: "short" });
  return `${weekday} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

/** Tab "Uplatniť poukaz": paste a gift voucher code; credit is added, a workshop voucher offers workshops to pick. */
export function RedeemVoucher({ onRedeemed }: RedeemVoucherProps) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<WorkshopChoice | null>(null);
  const [bookingSlotId, setBookingSlotId] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setChoice(null);
    const { data, error } = await redeemVoucher(trimmed);
    setLoading(false);
    if (error || !data) {
      setError(error?.message ?? "Poukaz sa nepodarilo uplatniť.");
      return;
    }
    if (data.type === "credit") {
      setCode("");
      onRedeemed({ type: "credit", code: data.code, amountCents: data.amount_cents ?? 0, balanceCents: data.balance_cents ?? 0 });
      return;
    }
    setChoice({ code: data.code, kindLabel: data.workshop_kind_label ?? "", options: data.options ?? [] });
  };

  const handlePick = async (slotId: number) => {
    if (!choice) return;
    setBookingSlotId(slotId);
    setError(null);
    const { data, error } = await redeemVoucher(choice.code, slotId);
    setBookingSlotId(null);
    if (error || !data || !data.booked) {
      setError(error?.message ?? "Termín sa nepodarilo rezervovať.");
      return;
    }
    setCode("");
    setChoice(null);
    onRedeemed({ type: "workshop", code: data.code, kindLabel: data.workshop_kind_label ?? "", booked: data.booked });
  };

  return (
    <div className="redeem-voucher-page">
      <h2>Uplatniť darčekový poukaz</h2>
      <p className="redeem-voucher-intro">
        Zadajte kód z poukazu. Kreditový poukaz sa pripíše k vášmu kreditu a automaticky sa použije pri najbližšej rezervácii.
        Pri poukaze na workshop si vyberiete termín.
      </p>
      <form className="redeem-voucher" onSubmit={handleSubmit}>
        <label htmlFor="voucher-code" className="redeem-voucher-label">Kód poukazu</label>
        <div className="redeem-voucher-row">
          <input
            id="voucher-code"
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); setChoice(null); }}
            placeholder="napr. 7F3K9Q"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={32}
            disabled={loading || bookingSlotId !== null}
          />
          <button type="submit" className="btn btn-primary" disabled={loading || bookingSlotId !== null || !code.trim()}>
            {loading ? "Overujem..." : "Uplatniť"}
          </button>
        </div>
        {error && !choice && <div className="form-error">{error}</div>}
      </form>

      {choice && (
        <div className="redeem-workshop-choice">
          <h3>Poukaz na workshop {choice.kindLabel}</h3>
          {choice.options.length === 0 ? (
            <p className="redeem-workshop-empty">
              Momentálne nie je vypísaný žiadny termín workshopu {choice.kindLabel} s voľným miestom.
              Poukaz ostáva platný, skúste to neskôr.
            </p>
          ) : (
            <>
              <p className="redeem-voucher-intro">Vyberte termín. Poukaz platí na jedno miesto a po výbere sa použije.</p>
              <div className="redeem-workshop-list">
                {choice.options.map((o) => (
                  <div key={o.slot_id} className="redeem-workshop-item">
                    <div className="redeem-workshop-when">
                      <strong>{formatWorkshopDate(o.date)}</strong> {o.start_time.slice(0, 5)}–{o.end_time.slice(0, 5)}
                    </div>
                    <div className="redeem-workshop-title">{o.title}</div>
                    <div className="redeem-workshop-free">
                      {o.available === 1 ? "posledné miesto" : `${o.available} voľných miest`}
                    </div>
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => handlePick(o.slot_id)}
                      disabled={bookingSlotId !== null}
                    >
                      {bookingSlotId === o.slot_id ? "Rezervujem..." : "Vybrať"}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {error && <div className="form-error">{error}</div>}
        </div>
      )}
    </div>
  );
}
